import { useCallback, useState } from 'react';

import type { PhoneNumber } from '#src/app/shared/utils/phoneCollection';

import type { PhoneOptInEventHandlers } from '../events';
import type { PhoneOptInOutcome, PhoneOptInResult, PhoneOptInScreen } from '../types';
import type { UseConsentReturn } from '../useConsent';

type UseSubmitPhoneOptInParams = Readonly<{
	screen: PhoneOptInScreen;
	consent: UseConsentReturn;
	/** Tracking-property name the consent boolean is reported under; differs per screen. */
	consentTrackingKey: string;
	events: PhoneOptInEventHandlers;
	onComplete: (result: PhoneOptInResult) => void;
}>;

/**
 * Which ask is in flight, so a surface can put the spinner on the control the starter actually
 * pressed. A single "is submitting" boolean can't say that.
 *
 * Keyed on the function called, not on the outcome: continuing past an empty field persists a
 * refusal but the starter pressed the primary, so it reports `submit`.
 */
export type PhoneOptInPendingAction = 'submit' | 'decline';

type UseSubmitPhoneOptInReturn = Readonly<{
	pendingAction: PhoneOptInPendingAction | null;
	isSubmitting: boolean;
	submit: (
		phoneNumberInput: PhoneNumber,
		userPhoneNumber: PhoneNumber,
		trackingProps: Readonly<Record<string, unknown>>,
	) => Promise<void>;
	decline: (userPhoneNumber: PhoneNumber, trackingProps: Readonly<Record<string, unknown>>) => Promise<void>;
}>;

/**
 * The "save consent, then report the outcome" tail shared by both opt-in screens.
 *
 * Both ways of moving on without opting in — continuing past an empty field, and an explicit
 * refusal — persist a refusal. Only an actual opt-in reports `consented`, which is what a surface
 * uses to decide whether the ask is settled.
 *
 * Where to go next is the caller's `onComplete`, never a direct call into a flow — that is what
 * lets a screen render outside a stepped flow.
 */
export function useSubmitPhoneOptIn({
	screen,
	consent,
	consentTrackingKey,
	events,
	onComplete,
}: UseSubmitPhoneOptInParams): UseSubmitPhoneOptInReturn {
	const [pendingAction, setPendingAction] = useState<PhoneOptInPendingAction | null>(null);

	// `saved_consent` and the consent-name key mean "consent was granted", so a refusal reports both
	// as `false` even though the refusal itself is persisted.
	//
	// `action` is which control the starter pressed, not what got recorded: continuing past an empty
	// field lands here too, and the spinner belongs on the primary that was pressed.
	const recordDecline = useCallback(
		async (
			action: PhoneOptInPendingAction,
			userPhoneNumber: PhoneNumber,
			trackingProps: Readonly<Record<string, unknown>>,
		) => {
			setPendingAction(action);
			let recorded = false;
			try {
				recorded = await consent.saveDecline();
			} catch {
				events.onSaveError(screen);
			} finally {
				setPendingAction(null);
			}
			// Completes either way: a failed write must never trap the user on the screen. It reports
			// `failed` rather than `declined` because nothing was persisted, so the ask is still
			// outstanding.
			const result: PhoneOptInResult = {
				screen,
				phoneNumber: userPhoneNumber,
				outcome: recorded ? 'declined' : 'failed',
				trackingProps: { ...trackingProps, saved_consent: false, [consentTrackingKey]: false },
			};
			events.onSubmitted(result);
			onComplete(result);
		},
		[screen, consent, consentTrackingKey, events, onComplete],
	);

	const submit = useCallback(
		async (
			phoneNumberInput: PhoneNumber,
			userPhoneNumber: PhoneNumber,
			trackingProps: Readonly<Record<string, unknown>>,
		) => {
			// Continuing past an empty field is a refusal, not an absence of an answer: the field is
			// optional, so moving on without filling it is how a starter says no. Recorded the same way
			// as an explicit "No thanks".
			if (!phoneNumberInput) {
				await recordDecline('submit', userPhoneNumber, trackingProps);
				return;
			}

			setPendingAction('submit');
			let phoneNumber = userPhoneNumber;
			let outcome: PhoneOptInOutcome = 'failed';
			try {
				const saved = await consent.saveConsent(phoneNumberInput, userPhoneNumber);
				if (saved) {
					phoneNumber = phoneNumberInput;
					outcome = 'consented';
				}
			} catch {
				events.onSaveError(screen);
			} finally {
				setPendingAction(null);
			}

			const saved = outcome === 'consented';
			const result: PhoneOptInResult = {
				screen,
				phoneNumber,
				outcome,
				trackingProps: { ...trackingProps, saved_consent: saved, [consentTrackingKey]: saved },
			};
			events.onSubmitted(result);
			onComplete(result);
		},
		[screen, consent, consentTrackingKey, events, onComplete, recordDecline],
	);

	const decline = useCallback(
		async (userPhoneNumber: PhoneNumber, trackingProps: Readonly<Record<string, unknown>>) =>
			recordDecline('decline', userPhoneNumber, trackingProps),
		[recordDecline],
	);

	return { pendingAction, isSubmitting: pendingAction !== null, submit, decline };
}
